import classNames from 'classnames';
import { Feature } from 'geojson';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import TripRouteAccordion from '@/components/modules/trip-route-details/trip-route-accordion/trip-route-accordion';
import {
  extractDescriptionItems,
  extractLandmarkItems,
  extractTransportModeItems,
  getMapMarkerIndex,
  isDescriptionAndTransportList,
  isDescriptionItem,
  isLandmarkItem,
  isTransportModeItem,
  mapLandmarksFeatureCollectionAdapter,
  mapMarkersFeatureAdapter,
  mapMarkersFeatureCollectionAdapter,
  transportModesFeatureCollectionAdapter,
} from '@/components/modules/trip-route-details/trip-route-details.adapter';
import TripRouteDetailsDescription from '@/components/modules/trip-route-details/trip-route-details-description/trip-route-details-description';
import TripRouteDetailsModal from '@/components/modules/trip-route-details/trip-route-details-modal/trip-route-details-modal';
import TripRouteDetailsTransportMode from '@/components/modules/trip-route-details/trip-route-details-transport-mode/trip-route-details-transport-mode';
import Button from '@/components/molecules/button/button';
import LayoutPicker, {
  Layout,
  LayoutPickerItem,
} from '@/components/molecules/layout-picker/layout-picker';
import Map, { MapHandle } from '@/components/molecules/map/map';
import { CtaLinkListProps } from '@/components/organisms/cta-link-list/cta-link-list';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import useIsScrollCompleted from '@/libraries/hooks/use-is-scroll-completed';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import TripRouteAccordionGroup from './trip-route-accordion-group/trip-route-accordion-group';
import styles from './trip-route-details.module.scss';

export interface TripRouteDetailsProps extends ModuleWrapperProps {
  /** Indicates whether to disable rendering the map */
  disableMap: Content.Components['DayToDayDescriptionAndRouteMapModuleDto']['disableMap'];
  /** The text to show as a headline */
  headline?: Content.Components['DayToDayDescriptionAndRouteMapModuleDto']['headline'];
  /** The image to show as a placeholder for the map */
  image: Content.Components['DayToDayDescriptionAndRouteMapModuleDto']['image'];
  /** The number of items to show as accordion item */
  itemLimit?: Content.Components['DayToDayDescriptionAndRouteMapModuleDto']['itemLimit'];
  /** The list of accordion items to show */
  items?: Content.Components['DayToDayDescriptionAndRouteMapModuleDto']['items'];
  /** Indicates whether the zoom level should be set to a custom level */
  overrideZoomLevel: Content.Components['DayToDayDescriptionAndRouteMapModuleDto']['overrideZoomLevel'];
  /** Indicates whether the markers should be connected */
  removeLines: Content.Components['DayToDayDescriptionAndRouteMapModuleDto']['removeLines'];
}

export interface SelectedItemDetails {
  ctaLinks?: CtaLinkListProps['items'];
  currentCardIndex?: number;
  headline?: string;
  id?: string;
  imageCaption?: string;
  images?: Content.Properties.Image[] | null;
  preHeadline?: string;
  subHeadline?: string;
  text?: string | null;
  totalItemsCount?: number;
}

export interface InteractedItem {
  isActive: boolean;
  itemId: string;
}

const OFFSET = 23;

const TripRouteDetails = ({
  disableMap = false,
  headline,
  image,
  itemLimit,
  items = [],
  overrideZoomLevel,
  removeLines = false,
  ...rest
}: TripRouteDetailsProps) => {
  const { t } = useI18n();
  const isLaptop = useWindowSize('laptop');
  const mapRef = useRef<MapHandle | null>(null);
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const dataContainerRef = useRef<HTMLDivElement>(null);

  const { handleUpdateIsScrolled, stickyTopContentHeight } =
    usePageDetailsContext();

  const initialSelectedItemDetails: SelectedItemDetails = useMemo(() => {
    return {
      ctaLinks: [],
      currentCardIndex: undefined,
      headline: undefined,
      id: undefined,
      imageCaption: undefined,
      images: [],
      preHeadline: undefined,
      subHeadline: undefined,
      text: undefined,
      totalItemsCount: undefined,
    };
  }, []);

  const [showAll, setShowAll] = useState(false);
  const [renderMap, setRenderMap] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isMapHovered, setIsMapHovered] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [layout, setLayout] = useState<'list' | 'map'>('list');
  const [scrollTarget, setScrollTarget] = useState<number | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [interactedItem, setInteractedItem] = useState<InteractedItem>();
  const [activeAccordionId, setActiveAccordionId] = useState<string>();
  const [activeLandmarkId, setActiveLandmarkId] = useState<string>();
  const [selectedMarkerDetails, setSelectedMarkerDetails] =
    useState<SelectedItemDetails>(initialSelectedItemDetails);
  const [selectedLandmarkDetails, setSelectedLandmarkDetails] =
    useState<SelectedItemDetails>(initialSelectedItemDetails);

  const hasHiddenItems =
    itemLimit !== undefined && itemLimit > 0 && itemLimit < items?.length;

  const layoutItems: LayoutPickerItem[] = useMemo(() => {
    return [
      {
        icon: 'ListView',
        value: 'list',
      },
      {
        icon: 'Location',
        value: 'map',
      },
    ];
  }, []);

  const descriptionItems = useMemo(
    () => extractDescriptionItems(items),
    [items],
  );

  const landmarkItems = useMemo(() => extractLandmarkItems(items), [items]);

  const transportModeItems = useMemo(
    () => extractTransportModeItems(items),
    [items],
  );

  const formattedMapMarkers = useMemo(() => {
    return mapMarkersFeatureCollectionAdapter(descriptionItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptionItems]);

  const formattedMapLandmarks = useMemo(() => {
    return mapLandmarksFeatureCollectionAdapter(landmarkItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landmarkItems]);

  const formattedTransportModes = useMemo(() => {
    return transportModesFeatureCollectionAdapter(transportModeItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptionItems]);

  const activeItem: Feature | any = useMemo(() => {
    const selectedItem = descriptionItems?.find(
      item => item.pointOfInterest?.id === activeAccordionId,
    );

    return selectedItem ? mapMarkersFeatureAdapter(selectedItem) : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccordionId, descriptionItems]);

  useIsScrollCompleted({
    isEnabled: !!scrollTarget,
    onComplete: () => {
      setIsScrolling(false);
      setScrollTarget(null);
    },
    targetScrollY: scrollTarget ?? 0,
    threshold: 2,
  });

  const scrollToItem = (id: string) => {
    if (!window) return;

    const targetElement = document.getElementById(id);

    if (!targetElement) return;

    const offset =
      window.scrollY +
      targetElement.getBoundingClientRect().top -
      stickyTopContentHeight -
      OFFSET;

    setScrollTarget(offset);

    window.scrollTo({ behavior: 'smooth', top: offset });
  };

  // ! NOTE: Controls the sticky top content visibility change during scroll
  useEffect(() => {
    if (isScrolling) return;

    if (isMapHovered) return;

    handleUpdateIsScrolled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrolling, isMapHovered]);

  const getIsHidden = (index: number) => {
    return (
      !showAll && itemLimit !== undefined && itemLimit > 0 && index >= itemLimit
    );
  };

  const updateInteractedItem = (id: string) => {
    setShowAll(previousValue => {
      const isHiddenItem =
        itemLimit !== undefined && getMapMarkerIndex(items, id) >= itemLimit;

      if (!previousValue && isHiddenItem) {
        return true;
      }

      return previousValue;
    });

    setInteractedItem(previousValue => {
      if (previousValue && previousValue?.itemId === id) {
        return { ...previousValue, isActive: !previousValue?.isActive };
      } else {
        return { isActive: true, itemId: id };
      }
    });
  };

  useEffect(() => {
    const selectedItem = descriptionItems?.find(
      item => item.pointOfInterest?.id === activeAccordionId,
    );

    const itemIndex = selectedItem && descriptionItems.indexOf(selectedItem);

    const details: SelectedItemDetails = {
      ctaLinks: [],
      currentCardIndex: itemIndex,
      headline: selectedItem?.headline,
      id: selectedItem?.pointOfInterest?.id,
      imageCaption: selectedItem?.imageCaption,
      images: selectedItem?.images,
      preHeadline: selectedItem?.preHeadline,
      subHeadline: selectedItem?.subHeadline,
      text: selectedItem?.text,
      totalItemsCount: descriptionItems?.length,
    };

    setSelectedMarkerDetails(details);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccordionId]);

  useEffect(() => {
    if (!activeLandmarkId) return;

    const selectedItem = landmarkItems?.find(
      item => item.pointOfInterest?.id === activeLandmarkId,
    );

    const details: SelectedItemDetails = {
      ctaLinks: selectedItem?.ctaLinks,
      currentCardIndex: undefined,
      headline: selectedItem?.headline,
      id: selectedItem?.pointOfInterest?.id,
      imageCaption: selectedItem?.imageCaption,
      images: selectedItem?.images,
      preHeadline: selectedItem?.preHeadline,
      subHeadline: selectedItem?.subHeadline,
      text: selectedItem?.text,
      totalItemsCount: landmarkItems?.length,
    };

    setSelectedLandmarkDetails(details);
    setIsDetailsModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLandmarkId]);

  const handleClickShowMore = () => {
    if (showAll) {
      setIsScrolling(true);
      handleUpdateIsScrolled(false);
      scrollToItem('header');
    }

    setShowAll(previous => !previous);
  };

  // ! NOTE: handleScroll function takes the animation duration into consideration
  const handleScroll = (value?: boolean) => {
    if (!mapWrapperRef.current || !dataContainerRef.current) return;

    if (!value || !renderMap || !activeAccordionId) return;

    setIsScrolling(true);
    handleUpdateIsScrolled(false);

    setTimeout(() => {
      scrollToItem(`accordion-active`);
    }, 250);
  };

  const handleClickLayoutPicker = (value: Layout) => {
    if (value === 'map') {
      if (!renderMap) {
        // Update the UI immediately with flushSync
        flushSync(() => setRenderMap(true));
      }

      mapRef.current?.enterFullscreen();
    }

    if (value === 'list') {
      mapRef.current?.exitFullscreen();
    }
  };

  const handleClickAccordionItem = (id?: string) => {
    setActiveAccordionId(prevItem => {
      if (prevItem === id) {
        return undefined;
      } else {
        return id;
      }
    });
  };

  const handleClickMapMarker = (feature: Feature | any) => {
    updateInteractedItem(feature.properties.id);
  };

  const handleClickDescriptionCardNavigation = (id: number) => {
    if (!descriptionItems) return;

    const itemId = descriptionItems[id]?.pointOfInterest?.id;

    if (!itemId) return;

    updateInteractedItem(itemId);
  };

  const handleClickMapLandmark = (feature: Feature | any) => {
    setActiveLandmarkId(prevItem => {
      if (prevItem === feature.properties.id) {
        return undefined;
      } else {
        return feature.properties.id;
      }
    });
  };

  const handleDetailsModalOpen = (value: boolean) => {
    setIsDetailsModalOpen(value);
    setActiveLandmarkId(undefined);
  };

  const handleMouseEnter = () => {
    setIsMapHovered(true);
    handleUpdateIsScrolled(false);
  };

  const handleMouseLeave = () => {
    setIsMapHovered(false);
  };

  useEffect(() => {
    setLayout(isFullscreen ? 'map' : 'list');
  }, [isFullscreen]);

  useEffect(() => {
    if (!window || !renderMap || !mapRef.current || !mapWrapperRef.current)
      return;

    const mapWrapper = mapWrapperRef.current;

    mapRef.current.onFullscreenChange(setIsFullscreen);

    mapWrapper.addEventListener('mouseenter', handleMouseEnter);
    mapWrapper.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      mapWrapper.removeEventListener('mouseenter', handleMouseEnter);
      mapWrapper.removeEventListener('mouseleave', handleMouseLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMap]);

  const getMapContent = () => {
    const mapOverlayContent = () => {
      if (!isFullscreen) return;

      return (
        <React.Fragment>
          <div className={styles.fullScreenTop}>
            <LayoutPicker
              activeLayout={layout}
              items={layoutItems}
              onClick={layout => handleClickLayoutPicker(layout)}
            />
          </div>

          <div className={styles.fullScreenBottom}>
            <TripRouteDetailsDescription
              details={selectedMarkerDetails}
              onClick={value => handleClickDescriptionCardNavigation(value)}
            />
          </div>
        </React.Fragment>
      );
    };

    const mapContent = () => (
      <div
        ref={mapWrapperRef}
        className={classNames(styles.mapWrapper)}
        style={{ top: `${stickyTopContentHeight + OFFSET}px` }}
      >
        <Map
          ref={mapRef}
          activeItem={activeItem}
          connectMarkers={!removeLines}
          controlPosition={isLaptop ? 'bottom-right' : undefined}
          flyToZoom={overrideZoomLevel}
          items={{
            landmarks: formattedMapLandmarks,
            markers: formattedMapMarkers,
          }}
          onClickLandmark={handleClickMapLandmark}
          onClickMarker={handleClickMapMarker}
          transportModes={formattedTransportModes}
        >
          {mapOverlayContent()}
        </Map>
      </div>
    );

    const imageContent = (
      <div className={styles.imageWrapper}>
        <div className={styles.placeholder}>
          {image && (
            <Image
              alt={image.altText}
              aspectRatio="oneByOne"
              focalPoint={image.focalPoint}
              src={image.url}
              srcSet={{
                desktop: {
                  width: 515,
                },
                laptop: {
                  width: 355,
                },
                tablet: {
                  width: 672,
                },
              }}
              unoptimized
              width={296}
            />
          )}

          {!disableMap && (
            <Button
              className={styles.placeholderButton}
              onClick={() => setRenderMap(true)}
              text={t('items.common.words.showMap')}
            />
          )}
        </div>
      </div>
    );

    return (
      <React.Fragment>{renderMap ? mapContent() : imageContent}</React.Fragment>
    );
  };

  const getDescriptionItem = (
    item: Content.Properties.DescriptionItem,
    itemIndex: number,
  ) => {
    return (
      <TripRouteAccordion
        key={itemIndex}
        className={classNames(styles.accordion, {
          [styles.isHidden]: getIsHidden(itemIndex),
        })}
        interactedItem={interactedItem}
        item={item}
        onChangeShouldScroll={value => handleScroll(value)}
        onSelect={id => handleClickAccordionItem(id)}
      />
    );
  };

  const getDescriptionAndTransportList = (
    item: Content.Properties.DescriptionAndTransportList,
    itemIndex: number,
  ) => {
    if (!item.nestedItems) return;

    const validSubItems = item.nestedItems.filter(isDescriptionItem);

    return (
      <TripRouteAccordion
        key={itemIndex}
        className={classNames(styles.accordion, {
          [styles.isHidden]: getIsHidden(itemIndex),
        })}
        interactedItem={interactedItem}
        onChangeShouldScroll={value => handleScroll(value)}
        onSelect={id => handleClickAccordionItem(id)}
        subItems={validSubItems}
      />
    );
  };

  return (
    <ModuleWrapper
      {...rest}
      className={styles.component}
      settings={{
        anchorLinkSettings: rest.settings?.anchorLinkSettings ?? null,
        brand: rest.settings?.brand ?? '',
        showBottomIllustrations:
          rest.settings?.showBottomIllustrations ?? false,
        showTopIllustrations: rest.settings?.showTopIllustrations ?? false,
        showTransparentBackground:
          rest.settings?.showTransparentBackground ?? false,
        theme: rest.settings?.theme ?? null,
      }}
    >
      <Container>
        <Row className={classNames(styles.row, styles.isHeader)} id="header">
          <Column className={styles.headerColumn}>
            {headline && (
              <Typography
                className={styles.headline}
                tag="h2"
                tagStyle="displayMedium"
              >
                {headline}
              </Typography>
            )}
          </Column>

          <Column className={styles.headerColumn}>
            {!isLaptop && (
              <LayoutPicker
                activeLayout={layout}
                items={layoutItems}
                onClick={layout => handleClickLayoutPicker(layout)}
              />
            )}
          </Column>
        </Row>

        <Row className={classNames(styles.row, styles.isContent)}>
          <Column
            className={classNames(styles.contentColumn, {
              [styles.hasHiddenItems]: !showAll && hasHiddenItems,
            })}
            width={{ laptop: 6 }}
          >
            <div ref={dataContainerRef}>
              <TripRouteAccordionGroup>
                {items
                  ?.filter(item => !isLandmarkItem(item))
                  .map((item, itemIndex) => {
                    if (isDescriptionItem(item)) {
                      return getDescriptionItem(item, itemIndex);
                    }

                    if (isDescriptionAndTransportList(item)) {
                      return getDescriptionAndTransportList(item, itemIndex);
                    }

                    if (isTransportModeItem(item)) {
                      return (
                        <TripRouteDetailsTransportMode
                          key={itemIndex}
                          className={classNames(styles.transportMode, {
                            [styles.isHidden]: getIsHidden(itemIndex),
                          })}
                          isStandAlone
                          item={item}
                        />
                      );
                    }
                  })}
              </TripRouteAccordionGroup>
            </div>

            {hasHiddenItems && (
              <div className={styles.buttonContainer}>
                <Button
                  onClick={() => handleClickShowMore()}
                  text={
                    showAll
                      ? t('items.common.words.showLess')
                      : `${t('items.modules.dayToDayDescription.viewTrip')}`
                  }
                  variant="secondary"
                />
              </div>
            )}
          </Column>

          <Column className={styles.contentColumn} width={{ laptop: 6 }}>
            {getMapContent()}
          </Column>
        </Row>
      </Container>

      <TripRouteDetailsModal
        details={selectedLandmarkDetails}
        isOpen={isDetailsModalOpen}
        setIsOpen={value => handleDetailsModalOpen(value)}
      />
    </ModuleWrapper>
  );
};

TripRouteDetails.displayName = 'TripRouteDetails';

export default TripRouteDetails;
